import { Component, createRef } from 'react';
import cloneDeep from 'lodash-es/cloneDeep';
import autoBind from 'react-autobind';
import { isMobile } from 'react-device-detect';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';

import {
  defaultChartLayoutData, defaultChartGraphData, defaultChartGraphLastPointData,
  hoverChartMarkerData,
} from '../../../../../../data/directory/topics/capitalStructure';
import InsiderSvc from '../../../../../../services/dbServices/InsiderSvc';
import { copy } from '../../../../../../helpers/copy';
import {
  getRanges, checkAndRoun,
} from '../../../../../../helpers/topicsHelpers';
import convertToDecimal from '../../../../../../helpers/convertToDecimal';

import Preloader from '../../../../../Preloader';
import Plot from '../../../../../Plot';
import ChartModal from './ChartModal';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const chartConfig = { responsive: true };

class CapitalCharts extends Component {
  constructor() {
    super();
    autoBind(this);

    this.state = {
      selectedPoint: {
        visible: false,
        date: null,
        x: 0,
        y: 0,
        showStories: true,
      },
      chartLayout: {
        height: 300,
        margin: {
          t: 5, b: 50, l: 20, r: 70,
        },
        ...defaultChartLayoutData,
      },
      isChartLoading: true,
      isChartUpdating: true,
      isChartLibrary: true,
      chartGraphArr: [defaultChartGraphData],
      lastPointData: null,
      isChartHidden: false,
    };

    this.plot = createRef();
    this.chartModalRef = createRef();
    this.hoverEventHandled = false;
  }

  async componentDidMount() {
    this.cfMainWrapper = document.querySelector('.cf-main-wrapper');
    this.onResize();
  }

  componentDidUpdate(prevProps) {
    const { activeFilter, width, firstChartData } = this.props;
    if (activeFilter !== prevProps.activeFilter) {
      this.callChartData({ updateAPI: true });
    }
    if (width !== prevProps.width) {
      this.onResize();
    }
    if (!firstChartData.loading && firstChartData.loading !== prevProps.firstChartData.loading) {
      this.callChartData({
        propData: firstChartData.data,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.onPageHover);
  }

  onPageHover(e) {
    if (!this.plot.current) return;

    const {
      current: {
        plot: {
          current: plot,
        },
      },
    } = this.plot;
    const { current: chartModalRef } = this.chartModalRef;

    if (
      !plot.contains(e.target) && chartModalRef && !chartModalRef.contains(e.target)
    ) {
      this.onModalClose();
    }
  }

  onChartHover(e, handleHoverEvent = true) {
    const date = e.points[0].x;
    const value = e.points[0].y;
    const { pointIndex } = e.points[0];
    const { pageX, pageY } = (
      (e.event.changedTouches && e.event.changedTouches[0])
      || e.event
    );
    const { width } = this.props;

    let x;
    if (width >= 2800) {
      if (pageX + 300 <= width) {
        x = pageX - 300 > 100 ? pageX - 300 : 100;
      } else {
        x = width - 600;
      }
    } else if (width > 300) {
      if (pageX + 150 <= width) {
        x = pageX - 150 > 50 ? pageX - 150 : 50;
      } else {
        x = width - 300;
      }
    } else {
      x = 50;
    }

    const y = pageY + 25;

    const { chartMainData } = this.state;
    const currentData = chartMainData[pointIndex] || chartMainData.find((obj) => obj.date === date);

    this.setState((state) => ({
      selectedPoint: {
        ...state.selectedPoint,
        visible: true,
        date,
        x,
        y,
        value,
        pointIndex,
        extraValues: currentData,
      },
    }));
    if (handleHoverEvent && !this.hoverEventHandled) {
      document.addEventListener('mousemove', this.onPageHover);
      this.hoverEventHandled = true;
    }
  }

  onChartLibraryLoading() {
    this.setState({
      isChartLibrary: false,
    });
  }

  onModalClose() {
    this.setState(({ selectedPoint }) => ({
      selectedPoint: {
        ...selectedPoint,
        visible: false,
      },
    }));
    if (this.hoverEventHandled) {
      document.removeEventListener('mousemove', this.onPageHover);
      this.hoverEventHandled = false;
    }
  }

  onResize() {
    const { chartLayout } = this.state;
    const { width } = this.props;
    const newObj = cloneDeep(chartLayout);
    if (width >= 2800) {
      newObj.margin = {
        t: 10, b: 100, l: 40, r: 140,
      };
      newObj.height = 600;
      newObj.xaxis.linewidth = 2;
      newObj.yaxis.linewidth = 2;
      newObj.xaxis.tickfont.size = 24;
      newObj.yaxis.tickfont.size = 24;
    } else {
      newObj.height = 300;
      newObj.xaxis.linewidth = 1;
      newObj.yaxis.linewidth = 1;
      newObj.xaxis.tickfont.size = 12;
      newObj.yaxis.tickfont.size = 12;
      if (width <= 767) {
        newObj.margin = {
          t: 5, b: 50, l: 10, r: 54,
        };
      } else {
        newObj.margin = {
          t: 5, b: 50, l: 20, r: 70,
        };
      }
    }
    this.setState({
      chartLayout: newObj,
    });
  }

  generateChartData(chartData = []) {
    const { chartType } = this.props;
    const valueKey = chartType === 'marketCap' ? 'marketCapitalization' : 'adjustedSharePrice';
    const valueKeySecond = chartType === 'marketCap' ? 'marketCapitalization' : 'sharePrice';
    const xData = [];
    const yData = [];
    const chartMainData = [];
    for (let i = 0; i < chartData.length; i += 1) {
      const currentDate = chartData[i];
      xData.push(currentDate.date);
      yData.push(currentDate[valueKey] || currentDate[valueKeySecond]);
      chartMainData.push(currentDate);
    }

    return {
      xData, yData, chartMainData,
    };
  }

  yTicksFormat(data) {
    const newData = copy(data);
    const yValues = newData.sort((a, b) => a - b);
    let min = Number(yValues[0]);
    const max = Number(yValues[yValues.length - 1]);
    const step = (max - min) / 8;
    min += step;
    const avg = min !== max ? (max + min) / 2 : max;
    return [
      min,
      (avg + min) / 2,
      avg,
      (avg + max) / 2,
      max,
    ].map((value) => (
      convertToDecimal(value)
    ));
  }

  async callChartData({ updateAPI, propData }) {
    const { activeFilter, companyNumber } = this.props;
    if (updateAPI) {
      this.setState({
        isChartUpdating: true,
      });
    }
    const params = {
      time_period: activeFilter.toLowerCase(),
      company_number: companyNumber,
    };

    let chartAPIData = await InsiderSvc.getCompanyGraphsShares(params);

    if (propData) {
      chartAPIData = propData;
    } else {
      chartAPIData = await InsiderSvc.getCompanyGraphsShares(params);
    }

    const { xData, yData, chartMainData } = this.generateChartData(chartAPIData);

    const chartGraph = {
      ...defaultChartGraphData,
      x: xData,
      y: yData,
    };

    const { chartLayout } = this.state;
    const newObj = cloneDeep(chartLayout);
    if (chartGraph.x.length) {
      newObj.xaxis.range = [chartGraph.x[0], chartGraph.x[chartGraph.x.length - 1]];
    }

    const customYTick = this.yTicksFormat(chartGraph.y);
    newObj.yaxis.tickvals = customYTick;
    newObj.yaxis.ticktext = customYTick.map((value) => checkAndRoun(value, 2, true, false));

    if (!xData.length) {
      this.setState({
        isChartHidden: true,
        isChartLoading: false,
        isChartUpdating: false,
        chartGraphArr: [chartGraph],
        lastPointData: null,
        chartMainData,
        chartLayout: newObj,
      });

      return;
    }

    const lastPointValue = yData[yData.length - 1];
    const chartGraphLastPoint = {
      ...defaultChartGraphLastPointData,
      x: [xData[0], xData[xData.length - 1]],
      y: [lastPointValue, lastPointValue],
    };

    const { min, max, lastPointTop } = this.calculatePoint({
      chartData: yData,
      currentValue: lastPointValue,
    });
    newObj.yaxis.range = [min, max];

    const lastPointData = {
      lastPrice: lastPointValue,
      lastPointTop,
    };
    this.setState({
      isChartLoading: false,
      isChartUpdating: false,
      isChartHidden: !xData.length,
      chartGraphArr: [chartGraph, chartGraphLastPoint],
      lastPointData,
      chartMainData,
      chartLayout: newObj,
    });
  }

  chartHeightCalculate() {
    const { width } = this.props;
    if (width >= 2800) return 490;
    return 245;
  }

  calculatePoint(props) {
    const { currentValue, chartData } = props;

    const { min, max } = getRanges(chartData);
    const step = this.chartHeightCalculate() / (max - min);

    return {
      min,
      max,
      lastPointTop: Math.round(((currentValue - min) * step) - 5),
    };
  }

  render() {
    const {
      isChartHidden, isChartLoading, isChartUpdating, isChartLibrary,
      chartGraphArr, chartLayout, selectedPoint, lastPointData,
    } = this.state;
    const { activeFilter } = this.props;

    const chartGraphMain = copy(chartGraphArr);
    if (selectedPoint.visible) {
      chartGraphMain.push({
        ...hoverChartMarkerData,
        x: [selectedPoint.date],
        y: [selectedPoint.value],
      });
    }

    return (
      <div className={cx('capital-charts', { loading: isChartLoading })}>
        {isChartLoading ? (
          <Preloader loading top="unset" />
        ) : (
          <div className={cx('capital-charts-holder', { loading: isChartUpdating })}>
            {isChartHidden ? (
              <div className={Styles['capital-charts-no-data']}>
                No data is available for this time period.
              </div>
            ) : (
              <>
                {lastPointData && !isChartLibrary ? (
                  <div
                    style={{ top: `${this.chartHeightCalculate() - lastPointData.lastPointTop}px` }}
                    className={Styles['capital-charts-holder-last-point']}
                  >
                    {checkAndRoun(lastPointData.lastPrice, 2, true)}
                  </div>
                ) : null}
                <Plot
                  data={chartGraphMain}
                  layout={chartLayout}
                  config={chartConfig}
                  onHover={!isMobile ? this.onChartHover : null}
                  onClick={this.onChartHover}
                  onLoad={this.onChartLibraryLoading}
                  ref={this.plot}
                  loaderClassname={cx('capital-charts-holder-loader')}
                />
              </>
            )}
          </div>
        )}

        {selectedPoint?.visible && (
          <ChartModal
            ref={this.chartModalRef}
            onClose={this.onModalClose}
            y={selectedPoint.y}
            x={selectedPoint.x}
            date={selectedPoint.date}
            currentValue={selectedPoint.value}
            extraValues={selectedPoint.extraValues}
            timeFilter={activeFilter}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ common }) => ({
  width: common.width,
});

export default connect(mapStateToProps, null)(CapitalCharts);
